import React, { useState, useEffect } from "react";
import { 
  ShieldCheck, 
  User, 
  Mail, 
  ShieldAlert, 
  Save, 
  Lock,
  Activity,
  History,
  Key
} from "lucide-react";
import AdminSidebar from "./AdminSidebar";
import SecurityModal from "../../components/SecurityModal";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { db } from "../../firebase/config";
import { ref, update } from "firebase/database";
import "./admin.css";

const AdminSettings = () => {
  const { currentUser, userData } = useAuth();
  const { showAlert } = useToast();
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    permissions: "READ_ONLY",
    status: "system_root"
  });

  const [loading, setLoading] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);

  useEffect(() => {
    if (userData) {
      setProfile({
        name: userData.name || "Global Administrator",
        email: userData.email || "",
        permissions: userData.permissions || "FULL_ACCESS",
        status: userData.status || "system_root"
      });
    }
  }, [userData]);

  const handleSaveProfile = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const userRef = ref(db, `users/${currentUser.uid}`);
      await update(userRef, {
        name: profile.name,
        email: profile.email
      });
      showAlert("Administrator profile synchronized successfully.", "success");
    } catch (error) {
      showAlert("Profile Synchronization Failure: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-shell">
      <AdminSidebar />

      <main className="admin-content">
        <header className="admin-header">
          <div className="header-info">
            <div className="title-row">
              <ShieldCheck size={28} className="icon-blue" />
              <h1>System Settings</h1>
            </div>
            <p>Configure platform-level variables and administrative security</p>
          </div>
          <div className="system-status glass-card">
            <ShieldAlert size={18} className="icon-blue" />
            <span>Sovereign Identity HUD</span>
          </div>
        </header>

        <div className="scrollable-content settings-grid-container">
          <div className="settings-grid">
            {/* Global Admin Profile Card */}
            <section className="settings-card glass-card">
              <div className="card-header">
                <User size={20} className="icon-blue" />
                <h3>Platform Controller</h3>
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label>Administrator Identity</label>
                  <input 
                    type="text" 
                    value={profile.name} 
                    onChange={(e) => setProfile({...profile, name: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Security Notification Email</label>
                  <input 
                    type="email" 
                    value={profile.email} 
                    onChange={(e) => setProfile({...profile, email: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>System Permissions Level</label>
                  <input 
                    type="text" 
                    value={profile.permissions} 
                    disabled
                    className="disabled-input"
                  />
                </div>
                <button 
                  className="premium-btn primary save-btn w-full mt-4" 
                  onClick={handleSaveProfile}
                  disabled={loading}
                >
                  <Save size={18} />
                  {loading ? "Persisting..." : "Synchronize Profile"}
                </button>
              </div>
            </section>

            {/* Platform Security Cluster */}
            <section className="settings-card glass-card">
              <div className="card-header">
                <Key size={20} className="icon-blue" />
                <h3>Access Control HUD</h3>
              </div>
              <div className="card-body">
                <div className="security-summary">
                  <Lock size={24} className="icon-blue mb-4" />
                  <p>Centralized authorization for administrative credentials and tactical security keys.</p>
                </div>
                <button 
                  className="premium-btn primary update-btn w-full mt-6" 
                  onClick={() => setShowSecurityModal(true)}
                >
                  Change Private Security Key
                </button>
              </div>
            </section>

            {/* System Audit Configuration */}
            <section className="settings-card glass-card">
              <div className="card-header">
                <Activity size={20} className="icon-blue" />
                <h3>System Integrity</h3>
              </div>
              <div className="card-body toggle-list">
                <div className="toggle-item">
                  <div className="toggle-text">
                    <span className="label">Live Audit Logging</span>
                    <span className="desc">Track clinician registry changes</span>
                  </div>
                  <label className="switch">
                    <input type="checkbox" checked={true} readOnly />
                    <span className="slider round"></span>
                  </label>
                </div>

                <div className="toggle-item">
                  <div className="toggle-text">
                    <span className="label">Unauthorized Entry Alerts</span>
                    <span className="desc">Notify for failed admin login attempts</span>
                  </div>
                  <label className="switch">
                    <input type="checkbox" checked={true} readOnly />
                    <span className="slider round"></span>
                  </label>
                </div>
              </div>
            </section>

            {/* Data Sovereignty Module */}
            <section className="settings-card glass-card">
              <div className="card-header">
                <History size={20} className="icon-blue" />
                <h3>System Backup</h3>
              </div>
              <div className="card-body">
                <p className="text-dim text-sm mb-4">Export the full platform state (registry, vitals, appointments) into an encrypted clinical JSON report.</p>
                <button className="premium-btn secondary w-full">
                  Generate Sovereign Data Report
                </button>
              </div>
            </section>
          </div>
        </div>
      </main>

      <SecurityModal 
        isOpen={showSecurityModal} 
        onClose={() => setShowSecurityModal(false)} 
      />
    </div>
  );
};

export default AdminSettings;
